import React, { useState, useEffect, Suspense, useTransition } from 'react';
import {
    FileBarChart,
    Users,
    Package,
    MapPin,
    Calculator,
    ArrowLeft,
    ArrowRightLeft,
    Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { cn } from '../../components/ui/utils';

// We'll placeholder these for now, following the same pattern as Settings
const WorkerReports = React.lazy(() => import('./WorkerReports'));
const ItemReports = React.lazy(() => import('./ItemReports'));
const LocationReports = React.lazy(() => import('./LocationReports'));
const CostAnalysis = React.lazy(() => import('./CostAnalysis'));
const ReplacementReport = React.lazy(() => import('./ReplacementReport'));
const TransferReports = React.lazy(() => import('./TransferReports'));

interface InventoryReportsProps {
    initialView?: 'main' | 'worker' | 'item' | 'location' | 'cost' | 'replacement' | 'transfer';
}

const InventoryReports: React.FC<InventoryReportsProps> = ({ initialView = 'main' }) => {
    const [view, setView] = useState<'main' | 'worker' | 'item' | 'location' | 'cost' | 'replacement' | 'transfer'>(initialView);
    const [isPending, startTransition] = useTransition();

    useEffect(() => {
        startTransition(() => {
            setView(initialView);
        });
    }, [initialView]);

    const handleViewChange = (newView: any) => {
        startTransition(() => {
            setView(newView);
        });
    };

    const reportOptions = [
        { id: 'worker', label: 'Worker Reports', icon: <Users size={16} /> },
        { id: 'item', label: 'Item Reports', icon: <Package size={16} /> },
        { id: 'location', label: 'Location Reports', icon: <MapPin size={16} /> },
        { id: 'cost', label: 'Cost Analysis', icon: <Calculator size={16} /> },
        { id: 'replacement', label: 'Replacement & Expiry', icon: <FileBarChart size={16} /> },
        { id: 'transfer', label: 'Transfer Reports', icon: <ArrowRightLeft size={16} /> },
    ];

    const renderView = () => {
        if (view === 'worker') return <WorkerReports onBack={() => handleViewChange('main')} />;
        if (view === 'item') return <ItemReports onBack={() => handleViewChange('main')} />;
        if (view === 'location') return <LocationReports onBack={() => handleViewChange('main')} />;
        if (view === 'cost') return <CostAnalysis onBack={() => handleViewChange('main')} />;
        if (view === 'replacement') return <ReplacementReport onBack={() => handleViewChange('main')} />;
        if (view === 'transfer') return <TransferReports onBack={() => handleViewChange('main')} />;

        if (view !== 'main') {
            return (
                <div className="space-y-6">
                    <Button
                        variant="ghost"
                        onClick={() => handleViewChange('main')}
                        className="flex items-center gap-2 text-gray-500 hover:text-gray-900 -ml-2"
                    >
                        <ArrowLeft size={16} />
                        Back to Reports
                    </Button>

                    <div className="bg-white rounded-[32px] border border-gray-100 shadow-sm p-20 flex flex-col items-center justify-center text-center animate-in fade-in zoom-in-95 duration-500">
                        <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-6">
                            {reportOptions.find(o => o.id === view)?.icon}
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">{reportOptions.find(o => o.id === view)?.label}</h2>
                        <p className="text-gray-500 mb-8 max-w-sm">
                            Detailed reporting for {reportOptions.find(o => o.id === view)?.label.toLowerCase()} is being generated.
                        </p>
                        <div className="w-full max-w-md bg-gray-50 h-2 rounded-full overflow-hidden">
                            <div className="bg-blue-600 h-full w-1/3 animate-pulse" />
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="bg-white rounded-[32px] border border-gray-100 shadow-sm p-6 sm:p-20 flex flex-col items-center justify-center text-center animate-in fade-in zoom-in-95 duration-500 min-h-[400px] sm:min-h-[500px]">
                <div className="mb-6">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-100 shadow-sm">
                        <FileBarChart size={28} className="text-gray-400 sm:w-8 sm:h-8" />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Reports</h2>
                    <p className="text-xs sm:text-sm text-gray-500 max-w-[250px] sm:max-w-sm mx-auto">Generate and export detailed inventory reports</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-lg mt-4 px-2">
                    {reportOptions.map((option) => (
                        <button
                            key={option.id}
                            onClick={() => handleViewChange(option.id as any)}
                            className="flex items-center justify-center gap-3 p-4 sm:p-3 bg-white hover:bg-gray-50 border border-gray-100 rounded-2xl sm:rounded-xl transition-all font-bold text-sm text-gray-700 shadow-sm hover:shadow-md active:scale-95"
                        >
                            <div className="text-gray-400 shrink-0">
                                {option.icon}
                            </div>
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <Suspense fallback={
            <div className="flex flex-col items-center justify-center p-20 min-h-[400px] text-gray-400 animate-in fade-in duration-500">
                <Loader2 className="w-10 h-10 animate-spin mb-4 text-blue-500 opacity-50" />
                <p className="text-sm font-medium">Loading report module...</p>
            </div>
        }>
            {renderView()}
        </Suspense>
    );
};

export default InventoryReports;
